import { useCallback, useMemo } from 'react';
import {
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button
} from '@heroui/react';
import type { ButtonProps } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useI18n } from '../client';
import clsx from 'clsx';
import type { Collapseable } from '../types';
import { debug } from '../client';

interface LanguageOption {
    code: string;
    name: string;
    flag?: string;
}

export type I18nSwitchColor = ButtonProps['color'];

export type I18nSwitchSize = ButtonProps['size'];

export type I18nSwitchVariant = ButtonProps['variant'];

export type I18nSwitchRadius = ButtonProps['radius']

export interface I18nSwitchProps extends Collapseable {
    color?: I18nSwitchColor;
    /** 是否显示国旗 */
    showFlag?: boolean;
    /** 按钮样式变体 */
    variant?: I18nSwitchVariant;
    /** 按钮大小 */
    size?: I18nSwitchSize;
    /** 按钮圆角 */
    radius?: I18nSwitchRadius;
    /** 自定义语言选项 */
    languages?: LanguageOption[];
    /** 自定义类名 */
    className?: string;
    /** 强制显示 */
    forceShow?: boolean;
    /** aria-label属性，用于屏幕阅读器 */
    'aria-label'?: string;
}

/**
 * 获取语言名称的基础映射
 */
function getLanguageName(code: string): string {
    const languageNames: Record<string, string> = {
        'en': 'English',
        'zh': '简体中文',
        'zh-TW': '繁體中文',
        'zh-HK': '繁體中文（香港）',
        'es': 'Español',
        'fr': 'Français',
        'de': 'Deutsch',
        'ja': '日本語',
        'ko': '한국어',
        'pt': 'Português',
        'ru': 'Русский',
        'ar': 'العربية',
        'it': 'Italiano',
        'nl': 'Nederlands',
        'pl': 'Polski',
        'tr': 'Türkçe',
        'th': 'ไทย',
        'vi': 'Tiếng Việt',
        'hi': 'हिन्दी'
    };

    return languageNames[code] || code.toUpperCase();
}

/**
 * 获取语言国旗的基础映射
 */
function getLanguageFlag(code: string): string {
    const languageFlags: Record<string, string> = {
        'en': '🇺🇸',
        'zh': '🇨🇳',
        'zh-TW': '🇹🇼',
        'zh-HK': '🇭🇰',
        'es': '🇪🇸',
        'fr': '🇫🇷',
        'de': '🇩🇪',
        'ja': '🇯🇵',
        'ko': '🇰🇷',
        'pt': '🇵🇹',
        'ru': '🇷🇺',
        'ar': '🇸🇦',
        'it': '🇮🇹',
        'nl': '🇳🇱',
        'pl': '🇵🇱',
        'tr': '🇹🇷',
        'th': '🇹🇭',
        'vi': '🇻🇳',
        'hi': '🇮🇳'
    };

    return languageFlags[code] || '🌐';
}

/**
 * 国际化语言切换组件
 * 兼容 @gulibs/vgrove-i18n 生成的资源
 */
export function I18nSwitch({
    color = "default",
    isCollapsed = false,
    showFlag = false,
    variant = 'flat',
    size = 'sm',
    radius = 'sm',
    languages,
    className,
    forceShow = false,
    'aria-label': ariaLabel,
}: I18nSwitchProps) {

    const { locale, setLocale, availableLocales } = useI18n();

    // 过滤可用的语言选项
    const availableLanguages = useMemo(() => {
        // 如果用户提供了自定义语言列表，使用用户提供的
        if (languages && languages.length > 0) {
            // 如果没有可用语言列表，返回用户提供的所有语言
            if (!availableLocales || availableLocales.length === 0) {
                return languages;
            }
            // 过滤出系统支持的语言
            return languages.filter(lang => availableLocales.includes(lang.code));
        }

        // 如果用户没有提供语言列表，从availableLocales生成基础语言选项
        if (availableLocales && availableLocales.length > 0) {
            return availableLocales.map(code => ({
                code,
                name: getLanguageName(code),
                flag: getLanguageFlag(code)
            }));
        }

        // 如果都没有，返回空数组
        return [];
    }, [languages, availableLocales]);

    // 当前语言
    const currentLanguage = useMemo(() => {
        const found = availableLanguages.find(lang => lang.code === locale);

        if (!found && availableLanguages.length > 0) {
            return availableLanguages[0];
        }

        return found;
    }, [locale, availableLanguages]);

    // 显示条件检查
    const shouldShow = useCallback(() => {
        // 强制显示模式
        if (forceShow) return true;

        // 没有当前语言时不显示
        if (!currentLanguage)
            return false;

        return true;
    }, [currentLanguage, forceShow]);

    // 语言切换处理函数
    const handleLanguageChange = async (keys: any) => {

        debug.i18n('🔄 I18nSwitch: 语言切换触发', { keys, currentLocale: locale, type: typeof keys });

        let selected: string | undefined;

        // 处理不同类型的keys参数
        if (keys instanceof Set) {
            selected = Array.from(keys)[0]?.toString();
            debug.i18n('🔄 I18nSwitch: Set类型处理', { selected });
        } else if (Array.isArray(keys)) {
            selected = keys[0]?.toString();
            debug.i18n('🔄 I18nSwitch: Array类型处理', { selected });
        } else if (typeof keys === 'string') {
            selected = keys;
            debug.i18n('🔄 I18nSwitch: String类型处理', { selected });
        } else {
            debug.error('❌ I18nSwitch: 无效的选择值类型:', keys);
            return;
        }

        debug.i18n('🎯 I18nSwitch: 处理后的选择值', { selected, locale, availableLocales });

        if (selected && selected !== locale) {
            if (!availableLocales || !availableLocales.includes(selected)) {
                debug.error('❌ I18nSwitch: 选择的语言不在可用列表中:', {
                    selected,
                    availableLocales
                });
                debug.error('❌ I18nSwitch: 选择的语言不在可用列表中:', {
                    selected,
                    availableLocales
                });
                return;
            }

            try {
                debug.i18n('🚀 I18nSwitch: 开始切换语言', { from: locale, to: selected });
                await setLocale(selected);
                debug.success('✅ I18nSwitch: 语言切换成功', { newLocale: selected });
            } catch (error) {
                debug.error('❌ I18nSwitch: 语言切换失败:', error);
            }
        } else {
            debug.i18n('ℹ️ I18nSwitch: 跳过语言切换', {
                reason: selected === locale ? '语言相同' : '选择值无效',
                selected,
                locale
            });
        }
    };

    if (!shouldShow() || !currentLanguage) {
        return null;
    }

    if (isCollapsed) {
        return (
            <Dropdown placement="bottom-end">
                <DropdownTrigger>
                    <Button
                        color={color}
                        isIconOnly
                        radius={radius}
                        variant={variant}
                        size={size}
                        className={className}
                        aria-label={ariaLabel || `当前语言: ${currentLanguage?.name || locale}`}
                    >
                        {showFlag && currentLanguage.flag ? (
                            <span
                                className={clsx({
                                    'text-base': size === 'sm',
                                    'text-lg': size === 'md',
                                    'text-xl': size === 'lg',
                                })}
                            >
                                {currentLanguage.flag}
                            </span>
                        ) : (
                            <Icon
                                icon="solar:translation-2-linear"
                                className={clsx({
                                    'text-lg': size === 'sm',
                                    'text-xl': size === 'md',
                                    'text-2xl': size === 'lg',
                                })}
                            />
                        )}
                    </Button>
                </DropdownTrigger>
                <DropdownMenu
                    aria-label={ariaLabel || "选择语言"}
                    selectedKeys={[locale]}
                    selectionMode="single"
                    onSelectionChange={handleLanguageChange}
                >
                    {availableLanguages.map(lang => (
                        <DropdownItem
                            key={lang.code}
                            startContent={showFlag && lang.flag ? (
                                <span className="text-lg">{lang.flag}</span>
                            ) : null}
                        >
                            {lang.name}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </Dropdown>
        );
    }

    return (
        <Dropdown placement="bottom-end">
            <DropdownTrigger>
                <Button
                    color={color}
                    variant={variant}
                    size={size}
                    radius={radius}
                    className={className}
                    aria-label={ariaLabel || `当前语言: ${currentLanguage?.name || locale}，点击切换语言`}
                    startContent={
                        showFlag && currentLanguage.flag ? (
                            <span
                                className={clsx({
                                    'text-base': size === 'sm',
                                    'text-lg': size === 'md',
                                    'text-xl': size === 'lg',
                                })}
                            >
                                {currentLanguage.flag}
                            </span>
                        ) : (
                            <Icon
                                icon="solar:translation-2-linear"
                                className={clsx({
                                    'text-lg': size === 'sm',
                                    'text-xl': size === 'md',
                                    'text-2xl': size === 'lg',
                                })}
                            />
                        )
                    }
                >
                    {currentLanguage.name}
                </Button>
            </DropdownTrigger>
            <DropdownMenu
                aria-label={ariaLabel || "选择语言"}
                selectedKeys={[locale]}
                selectionMode="single"
                onSelectionChange={handleLanguageChange}
            >
                {availableLanguages.map(lang => (
                    <DropdownItem
                        key={lang.code}
                        startContent={showFlag && lang.flag ? (
                            <span className="text-lg">{lang.flag}</span>
                        ) : null}
                        classNames={{ title: "font-bold", description: "text-gray-400" }}
                        description={lang.code.toUpperCase()}
                    >
                        {lang.name}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    );
}

// 便捷导出
export const LanguageSwitch = I18nSwitch;